import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Item, TripView, Where } from "./api.ts";
import { costWords, refsOf } from "./cardFacts.ts";
import { dayLabel, type ItemIconName, itemWhen } from "./format.ts";
import { useMouseDrag } from "./mouseDrag.ts";
import { policyNote } from "./paid.ts";
import { Count, factLine, ItemIcon } from "./ui.tsx";

// The card in full, read in sections (DESIGN.md, Views, "A card reads in sections"; features/map.md, "The card in
// sections"; the founder, 2026-10-26: "i struggled to find information easily on them, especially when there are a
// lot of details on the page"). Under its head: At a glance, a raised box with what's looked up on the way, each fact
// on its own labelled line; its buttons; a row of the sections it has, to jump to one, pinned under the card's bar
// once scrolled past and marking the section in view; then its sections, every one open, in one order, each only
// when it has something. Each fact shows once: what's At a glance isn't said again in a section. Details.tsx lays
// the card out with these.

// The sections a card can have, in their order, each with its heading's icon.
export const SECTIONS = [
  { key: "questions", title: "Questions", icon: "feedback" },
  { key: "booking", title: "Booking", icon: "lock" },
  { key: "todos", title: "To-dos", icon: "check" },
  { key: "there", title: "Getting there", icon: "route" },
  { key: "like", title: "What it's like", icon: "star" },
  { key: "notes", title: "Notes", icon: "note" },
  { key: "research", title: "Research", icon: "search" },
  { key: "files", title: "Files and links", icon: "book" },
] as const satisfies readonly { key: string; title: string; icon: ItemIconName }[];
export type SectionKey = (typeof SECTIONS)[number]["key"];
// The jump row shows on a card with this many sections or more.
export const JUMP_FROM = 3;

// One section of a card: the look of Ideas' and Bookings' sections (PageSection in ui.tsx, the same classes), between
// hairlines under its heading in the eyebrow style, here with its icon, and never folding. The heading takes the focus
// when the jump row lands on it, so it's read from there.
export function CardSection({
  id,
  section,
  count,
  edit,
  children,
}: {
  id: string;
  section: SectionKey;
  count?: number;
  // What "Change with your AI" reaches here (Details.tsx, `reach`).
  edit?: string;
  children: ReactNode;
}) {
  const { title, icon } = SECTIONS.find((s) => s.key === section) as (typeof SECTIONS)[number];
  return (
    <section
      className="tm-page-section tm-card-section"
      aria-labelledby={id}
      data-section={section}
      {...(edit ? { "data-edit": edit } : {})}
    >
      <h2 id={id} className="eyebrow tm-group-title tm-card-section-title" tabIndex={-1}>
        <ItemIcon name={icon} size={16} />
        <span>
          {title}
          {count !== undefined && <Count n={count} />}
        </span>
      </h2>
      {children}
    </section>
  );
}

// One of At a glance's lines: its label and what it says; `when` when it gives the item's when (its day and time, or
// its days), so the head's when chip goes (DESIGN.md, "A card reads in sections": the dates show once).
export interface GlanceLine {
  key: string;
  label: string;
  value: ReactNode;
  tone?: "sun";
  when?: true;
}

// At a glance: a raised box (Souvenir's raised paper, a hairline, the medium radius, as the profile's groups are),
// each fact on its own labelled line; none when it has none of them.
export function Glance({ lines }: { lines: GlanceLine[] }) {
  if (lines.length === 0) return null;
  return (
    <section className="tm-settings-card tm-glance" aria-label="At a glance">
      <dl className="tm-glance-lines">
        {lines.map((l) => (
          <div key={l.key} className="tm-glance-line" data-glance={l.key}>
            <dt className="tm-settings-label tm-glance-label">{l.label}</dt>
            <dd className={`callout tm-glance-value${l.tone === "sun" ? " tm-sun-text" : ""}`}>{l.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const time = (t: string) => t.slice(11, 16);
// A day and its time, as a stay's check-in says it: "Sat 5 Dec, 15:00".
const dayTime = (t: string) => `${dayLabel(t.slice(0, 10))}, ${time(t)}`;
const placeName = (p: Where | undefined) => p?.name ?? p?.address ?? null;
// What At a glance says of an item, in its order: a stay's check-in and check-out, a journey's leaving and arriving
// (time and place), a place's day and time and its hours; then for any kind its references (held once booked), its
// cancellation as it stands today, what it costs and what's left to pay, its address and its phone. Its when, said
// here, isn't said in the head too (`when` on its lines): a journey's leaving then says its day as well as its time.
export function glanceLines(item: Item, trip: TripView, now = new Date()): GlanceLine[] {
  const out: GlanceLine[] = [];
  const w = item.when;
  const timed = w && (w.precision === "exact" || w.precision === "window") ? w : null;
  if (item.type === "stay" && w) {
    const [checkIn, checkOut] = timed
      ? [dayTime(timed.start), timed.end ? dayTime(timed.end) : null]
      : w.precision === "days"
        ? [dayLabel(w.start), dayLabel(w.end)]
        : w.precision === "day"
          ? [dayLabel(w.day), null]
          : [null, null];
    if (checkIn) out.push({ key: "check-in", label: "Check-in", value: checkIn, when: true });
    if (checkOut) out.push({ key: "check-out", label: "Check-out", value: checkOut, when: true });
  }
  if (item.type === "move") {
    // Leaving with its day and time; arriving with its time alone on the same day, with its day too on another (a
    // night train, a long flight). Without times, its places alone, and the head says its day.
    const apart = Boolean(timed?.end && timed.end.slice(0, 10) !== timed.start.slice(0, 10));
    const leaves = [timed && dayTime(timed.start), placeName(item.from)].filter((p): p is string => Boolean(p));
    const end = timed?.end;
    const arrives = [end && (apart ? dayTime(end) : time(end)), placeName(item.to)].filter((p): p is string =>
      Boolean(p),
    );
    const when = timed ? ({ when: true } as const) : {};
    if (leaves.length > 0) out.push({ key: "leaves", label: "Leaves", value: factLine(leaves), ...when });
    if (arrives.length > 0) out.push({ key: "arrives", label: "Arrives", value: factLine(arrives), ...when });
  }
  if (item.type === "place" || item.type === "goal") {
    const when = itemWhen(w);
    if (when) out.push({ key: "when", label: "When", value: factLine(when.split(" · ")), when: true });
  }
  if (item.hours || item.closed)
    out.push({
      key: "hours",
      label: "Hours",
      value: item.hours ? factLine([item.hours, item.closed && `closed ${item.closed}`]) : `Closed ${item.closed}`,
    });
  // Its references only once it's been booked (booked, done, or cancelled since): an option back to being weighed
  // keeps them for next time (Not booked after all), unsaid.
  const held = ["booked", "done", "cancelled"].includes(item.status);
  const refs = held && item.booking?.ref ? refsOf(item.booking.ref) : [];
  if (refs.length > 0)
    out.push({
      key: "ref",
      label: refs.length > 1 ? "Refs" : "Ref",
      value: (
        <span className="tm-refs">
          {refs.map((r) => (
            <CopyRef key={r} text={r} />
          ))}
        </span>
      ),
    });
  const policy = item.status === "booked" ? policyNote(item, now) : null;
  if (policy)
    out.push({ key: "cancellation", label: "Cancellation", value: policy.text, ...(policy.soon && { tone: "sun" }) });
  const cost = costWords(item, trip.members.length);
  if (cost) out.push({ key: "cost", label: "Cost", value: cost });
  if (item.where?.address) out.push({ key: "address", label: "Address", value: item.where.address });
  const phone = item.contact?.phone;
  if (phone)
    out.push({
      key: "phone",
      label: "Phone",
      value: (
        <a className="tm-glance-link" href={`tel:${phone.replace(/[^\d+]/g, "")}`}>
          {phone}
        </a>
      ),
    });
  return out;
}

// A reference: a tap copies it, saying "Copied" for a moment; with no clipboard here (a page not served securely),
// it's there to select by hand.
function CopyRef({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => clearTimeout(timer.current ?? undefined), []);
  return (
    <span className="tm-ref">
      <button
        type="button"
        className="tm-ref-copy"
        aria-label={`Copy ${text}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
          } catch {
            return;
          }
          setCopied(true);
          clearTimeout(timer.current ?? undefined);
          timer.current = setTimeout(() => setCopied(false), 2000);
        }}
      >
        <span className="tm-ref-text">{text}</span>
        <ItemIcon name={copied ? "check" : "copy"} size={14} />
      </button>
      <span className="caption tm-ref-said" role="status">
        {copied ? "Copied" : ""}
      </span>
    </span>
  );
}

// The nearest ancestor that scrolls: the card's panel, its sheet's body or its modal's.
function scrollerOf(el: HTMLElement): HTMLElement | null {
  for (let e = el.parentElement; e; e = e.parentElement)
    if (/(auto|scroll)/.test(getComputedStyle(e).overflowY)) return e;
  return null;
}

// How far in from an edge the names fade where more are past it (app.css, .tm-jump-list): the marked one is kept
// clear of it.
const FADE = 32;

// The jump row: the names of the sections the card has, in their order, as text tabs on a hairline, the one in view
// in ink and underlined; a tap scrolls the card to that section's heading, under the row. Scrolled past, it stays
// pinned under the card's bar (Show on map, Share, the X: sticky on a computer; on a phone the sheet's bar is above
// what scrolls), marking the section in view. Where the names don't fit it scrolls sideways within itself, never
// wrapping and never widening the card, fading at an edge with more past it: on a phone by a swipe, on a computer by
// a drag of the mouse as well as the trackpad's sideways scroll (DESIGN.md, the log rows "The jump row drags" and
// "The jump row as text tabs").
export function JumpRow({ sections }: { sections: { key: SectionKey; id: string }[] }) {
  const nav = useRef<HTMLElement>(null);
  const list = useRef<HTMLUListElement>(null);
  // Where it pins, from the scroller's top: under the card's bar where that stays too.
  const top = useRef(0);
  const [pinned, setPinned] = useState(false);
  const [current, setCurrent] = useState<SectionKey | null>(null);
  // The section a tap went to, marked until the card is scrolled by hand: the last ones may not reach the top.
  const tapped = useRef<SectionKey | null>(null);
  const ids = sections.map((s) => `${s.key}:${s.id}`).join();
  const drag = useMouseDrag<HTMLUListElement>();
  // Whether names are past its start and its end, where it fades.
  const [more, setMore] = useState({ before: false, after: false });
  // biome-ignore lint/correctness/useExhaustiveDependencies: again when its sections change (`ids` says them)
  useLayoutEffect(() => {
    const ul = list.current;
    if (!ul) return;
    const edges = () =>
      setMore((was) => {
        const now = {
          before: ul.scrollLeft > 1,
          after: ul.scrollLeft + ul.clientWidth < ul.scrollWidth - 1,
        };
        return now.before === was.before && now.after === was.after ? was : now;
      });
    edges();
    const sized = new ResizeObserver(edges);
    sized.observe(ul);
    ul.addEventListener("scroll", edges, { passive: true });
    return () => {
      sized.disconnect();
      ul.removeEventListener("scroll", edges);
    };
  }, [ids]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: again when its sections change (`ids` says them)
  useLayoutEffect(() => {
    const row = nav.current;
    const scroller = row && scrollerOf(row);
    if (!row || !scroller) return;
    const bar = row.parentElement?.querySelector<HTMLElement>(":scope > .tm-details-bar") ?? null;
    let frame = 0;
    // A sticky top counts from inside the scroller's padding.
    const inside = () =>
      scroller.getBoundingClientRect().top + Number.parseFloat(getComputedStyle(scroller).paddingTop);
    const look = () => {
      frame = 0;
      const edge = inside() + top.current;
      const stuck = row.getBoundingClientRect().top - edge < 0.5 && scroller.scrollTop > 0;
      setPinned(stuck);
      if (tapped.current) return setCurrent(tapped.current);
      if (!stuck) return setCurrent(null);
      // The section in view: the last whose top has come up to a little under the row (a third of the way down what
      // shows under it, 120 px at most); scrolled to the end, the last whose heading shows.
      const box = scroller.getBoundingClientRect();
      const line = edge + row.offsetHeight;
      const reach = line + Math.min(120, Math.max(0, (box.bottom - line) / 3));
      const end = scroller.scrollTop >= scroller.scrollHeight - scroller.clientHeight - 2;
      let shown: SectionKey | null = null;
      for (const s of sections) {
        const at = document.getElementById(s.id)?.closest("section")?.getBoundingClientRect().top;
        if (at !== undefined && (at <= reach || (end && at < box.bottom - 48))) shown = s.key;
      }
      setCurrent(shown);
    };
    const measure = () => {
      // Under the card's bar where that stays too, else at the very top of what scrolls (a phone's sheet, its bar
      // above it), over the scroller's padding.
      const style = bar && getComputedStyle(bar);
      top.current =
        bar && style?.position === "sticky"
          ? (Number.parseFloat(style.top) || 0) + bar.offsetHeight
          : -Number.parseFloat(getComputedStyle(scroller).paddingTop);
      row.style.top = `${top.current}px`;
      look();
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(look);
    };
    // Scrolled by hand: the mark follows what's in view again.
    const byHand = () => {
      tapped.current = null;
    };
    measure();
    const sized = new ResizeObserver(measure);
    sized.observe(scroller);
    if (bar) sized.observe(bar);
    scroller.addEventListener("scroll", onScroll, { passive: true });
    for (const kind of ["wheel", "touchstart", "keydown"] as const)
      scroller.addEventListener(kind, byHand, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      sized.disconnect();
      scroller.removeEventListener("scroll", onScroll);
      for (const kind of ["wheel", "touchstart", "keydown"] as const) scroller.removeEventListener(kind, byHand);
    };
  }, [ids]);
  // The marked name kept in view in the row.
  useEffect(() => {
    const ul = list.current;
    const button = current && ul?.querySelector<HTMLElement>(`[data-jump="${current}"]`);
    if (!ul || !button) return;
    // Where it is in the list, and what of the list shows: within its reach past the card's words on either side.
    const at = button.getBoundingClientRect().left - ul.getBoundingClientRect().left + ul.scrollLeft;
    const [left, right] = [at, at + button.offsetWidth];
    const reach = Number.parseFloat(getComputedStyle(ul).paddingLeft) || 0;
    const [from, to] = [ul.scrollLeft + reach, ul.scrollLeft + ul.clientWidth - reach];
    const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (left < from + FADE) ul.scrollTo({ left: left - reach - FADE, behavior: still ? "auto" : "smooth" });
    else if (right > to - FADE)
      ul.scrollTo({ left: right + reach + FADE - ul.clientWidth, behavior: still ? "auto" : "smooth" });
  }, [current]);
  const jump = (key: SectionKey, id: string) => {
    const row = nav.current;
    const scroller = row && scrollerOf(row);
    const heading = document.getElementById(id);
    const section = heading?.closest("section");
    if (!row || !scroller || !heading || !section) return;
    // Its top hairline on the row's, the heading just under the row, pinned by then.
    const under =
      scroller.getBoundingClientRect().top +
      Number.parseFloat(getComputedStyle(scroller).paddingTop) +
      top.current +
      row.offsetHeight -
      1;
    tapped.current = key;
    setCurrent(key);
    scroller.scrollTo({
      top: scroller.scrollTop + section.getBoundingClientRect().top - under,
      behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
    heading.focus({ preventScroll: true });
  };
  return (
    <nav ref={nav} className={`tm-jump${pinned ? " is-pinned" : ""}`} aria-label="Sections">
      <ul
        ref={list}
        className={[
          "tm-jump-list",
          more.before && "is-more-before",
          more.after && "is-more-after",
          drag.dragging && "is-dragging",
        ]
          .filter(Boolean)
          .join(" ")}
        {...drag.handlers}
      >
        {sections.map((s) => (
          <li key={s.key}>
            <button
              type="button"
              className="tm-jump-to"
              data-jump={s.key}
              {...(current === s.key ? { "aria-current": "true" as const } : {})}
              onClick={() => jump(s.key, s.id)}
            >
              {SECTIONS.find((x) => x.key === s.key)?.title}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
